import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { createStack, Stack } from '@famly/mf_layout_stack';
import { media } from 'web-app/styleguide/utils';
import { hexToRGBString } from 'web-app/util/hex-to-rgb';
import PlatformHelper from 'web-app/helpers/platform-helper';
import { getSpacing, s1, s2, s6, s8, s10, s12, s15, s16 } from 'web-app/styleguide/spacing';
import { EspiraIcon } from 'web-app/react/components/icons/custom';
import { ESPIRA_ICON_CONFIG, getAsset } from 'web-app/shared/white-labeling-constants';
import { getWhitelabel } from 'web-app/shared/white-labeling-helpers';
import { createBox } from '@famly/mf_layout_box';
import { Text } from '@famly/mf_data-display_text';
import { useTransitionToPath } from '@famly/vet_navigation_route-config';
import { useRepeatedTap } from '@famly/vet_login_business-logic';
import { getFromLocalStorage } from '@famly/stat_ts-utils_local-storage';
import { LoginLanguagePicker } from './login-language-picker';
import { LANGUAGE_KNOWN_FROM_LOGIN_LOCAL_STORAGE_KEY } from './constants';
// Keeps a customer who stumbles on the gesture out of the debugger. It ships in the bundle, so it is no secret.
const DEBUGGER_PASSWORD = 'famly1234';
const DEBUGGER_PASSWORD_PROMPT = 'Password';
const LoginChrome = ({ children, locale, hasAccounts, loadingDeviceAccounts, }) => {
    const whitelabel = getWhitelabel();
    const transitionToPath = useTransitionToPath();
    // Reaches the debugger before login, e.g. to switch server for an account only on staging. Deliberately not
    // focusable or announced: the gesture is hidden from everyone.
    const openDebuggerOnRepeatedTap = useRepeatedTap({
        count: 7,
        onTrigger: () => {
            // oxlint-disable-next-line no-alert
            if (window.prompt(DEBUGGER_PASSWORD_PROMPT) === DEBUGGER_PASSWORD) {
                transitionToPath('/debugger');
            }
        },
    });
    // Device accounts or an earlier login here already tell us the language. The `locale` key cannot, since boot
    // saves the browser language into it. Waiting for device accounts keeps the picker from flashing on the PIN screen.
    const showLanguagePicker = !hasAccounts && !loadingDeviceAccounts && !getFromLocalStorage(LANGUAGE_KNOWN_FROM_LOGIN_LOCAL_STORAGE_KEY);
    return (_jsxs(LayoutContainer, { children: [_jsxs(Before, { children: [showLanguagePicker ? (_jsx(Stack, { direction: "row", justifyContent: "flex-end", flexShrink: 0, px: 4, pt: "calc(env(safe-area-inset-top) + 16px)", children: _jsx(LoginLanguagePicker, {}) })) : null, _jsxs(InnerContent, { children: [_jsx(Stack, { justifyContent: "center", alignItems: "center", flexShrink: 0, onClick: openDebuggerOnRepeatedTap, children: _jsx(Logo, { whitelabel: whitelabel, hasAccounts: hasAccounts }) }), children] }), PlatformHelper.isMobileApp() ? (_jsx(BottomContainer, { children: _jsx(Text, { variant: "body-small", component: "span", color: "n300", children: PlatformHelper.version() }) })) : null] }), _jsx(After, { children: _jsx(Placeholder, { locale: locale, isStaff: hasAccounts, loading: loadingDeviceAccounts }) })] }));
};
const Logo = ({ whitelabel, hasAccounts, }) => {
    const logoLogin = getAsset(whitelabel, 'logoLogin');
    // if config is found, use it
    if (logoLogin) {
        return _jsx(StandardLogo, { pb: 3, src: logoLogin });
    }
    // to support the original way
    switch (whitelabel) {
        case 'espira':
            return (_jsx(StyledEspiraIconContainer, { hasAccounts: hasAccounts, children: _jsx(StyledEspiraIcon, { width: 144, height: 140, spireTextColor: ESPIRA_ICON_CONFIG.SPIRE_PORTALEN_COLOR_GREEN, portalenTextColor: ESPIRA_ICON_CONFIG.SPIRE_PORTALEN_COLOR_RED, hasAccounts: hasAccounts }) }));
        case 'kido':
            return _jsx(StyledKidoLogo, { src: "img/kido/kido-blue.png" });
        case 'brighthorizons':
            return _jsx(StyledBrightHorizonsLogo, { hasAccounts: hasAccounts, src: "img/bright-horizons/login-logo.svg" });
        case 'kindred':
            return _jsx(StyledKindredLogo, { hasAccounts: hasAccounts, src: "img/kindred/logo-white-bg.svg" });
        case 'grandiruk':
            return _jsx(StyledGrandirUKLogo, { src: "img/grandiruk/GrandirLogo-WhiteBG.svg" });
        case 'nuffieldhealth':
            return _jsx(StyledNuffieldHealthLogo, { src: "img/nuffieldhealth/NuffieldLogo-WhiteBG.svg" });
        case 'nfamilyclub':
            return _jsx(StyledNFamilyClubLogo, { src: "img/nfamilyclub/logo-white-bg.svg" });
        case 'kinderzimmeruk':
            return _jsx(StyledKinderzimmerUKLogo, { src: "img/kinderzimmeruk/KinderzimmerUKLogoMark.svg" });
        case 'cpctreehouse':
            return _jsx(StyledCpcTreehouseLogo, { src: "img/cpc-treehouse/CpcTreehouseLogoMark.svg" });
        case 'cpcorchards':
            return _jsx(StyledCpcOrchardLogo, { src: "img/cpc-orchard/CpcOrchardLogoMark.svg" });
        case 'busybees':
            return _jsx(StyledBusyBeesLogo, { hasAccounts: hasAccounts, src: "img/busybees/BusyBeesLogo.svg" });
        case 'bbmontessori':
            return _jsx(StyledBBMontessoriLogo, { src: "img/bbmontessori/logo-white-bg.svg" });
        case 'bbodyssey':
            return _jsx(StyledBBOdysseyLogo, { src: "img/bbodyssey/logo-tagline-white-bg.svg" });
        case 'famly':
        case null:
        default:
            return _jsx(StyledFamlyLogo, { src: "img/famly-logos/Famly-Logo-WhiteBG.svg" });
    }
};
const StandardLogo = createBox({
    component: 'img',
    height: '100%',
    maxHeight: '150px',
    objectFit: 'contain',
    width: '100%',
    boxSizing: 'border-box',
});
const StyledEspiraIconContainer = styled.div `
    display: ${props => (props.hasAccounts ? 'none' : 'flex')};
    margin-bottom: ${props => (props.hasAccounts ? getSpacing(s2) : getSpacing(s8))};
    @media (min-height: 600px) {
        display: flex;
    }

    @media (min-height: 850px) {
        margin-top: ${props => (props.hasAccounts ? '0' : getSpacing(s15))};
        margin-bottom: ${getSpacing(s10)};
    }
`;
const StyledEspiraIcon = styled(EspiraIcon) `
    width: ${props => (props.hasAccounts ? '90px' : '160px')};
    height: ${props => (props.hasAccounts ? '80px' : '157px')};

    @media (min-height: 850px) {
        width: ${props => (props.hasAccounts ? '140px' : '200px')};
        height: ${props => (props.hasAccounts ? '130px' : '190px')};
    }
`;
const StyledKidoLogo = styled.img `
    margin-bottom: ${getSpacing(s16)};
    ${media.tablet `
        margin-bottom: ${getSpacing(s6)};
        @media screen and (min-aspect-ratio: 1024/530) {
            width: 130px;
        }
    `}
    ${media.mobile `&& {
        margin-bottom: ${getSpacing(s6)};
        height: 35px;
    }`}
    ${media.portrait `
        margin-bottom: ${getSpacing(s12)};
    `}
    ${media.notLandscape `
        @media (max-height: 900px) {
            margin-bottom: ${getSpacing(s8)};
        }
        @media (max-height: 600px) {
            height: 30px;
            width: auto;
            margin-bottom: ${getSpacing(s2)};
        }
    `}
`;
const StyledFamlyLogo = styled.img `
    width: 250px;
    margin-bottom: ${getSpacing(s16)};

    ${media.mobile `&& {
        margin-bottom: ${getSpacing(s6)};
        width: 220px;
    }`}
`;
const StyledBrightHorizonsLogo = styled.img `
    height: ${props => (props.hasAccounts ? '100px' : '150px')};
    width: auto;

    @media (min-height: 850px) {
        height: 192px;
        margin-bottom: ${getSpacing(s16)};
    }
`;
const StyledKindredLogo = styled.img `
    height: ${props => (props.hasAccounts ? '50px' : '80px')};
    width: auto;
    margin-bottom: ${props => (props.hasAccounts ? '24px' : '32px')};

    @media (min-height: 850px) {
        height: 125px;
        margin-bottom: ${getSpacing(s16)};
    }
`;
const StyledGrandirUKLogo = styled.img `
    height: 40px;
    width: auto;
    margin-bottom: ${getSpacing(s8)};

    @media (min-height: 850px) {
        height: 64px;
        margin-bottom: ${getSpacing(s16)};
    }

    @media (max-width: 700px) {
        margin-top: 0;
    }
`;
const StyledNuffieldHealthLogo = styled.img `
    height: 80px;
    width: auto;
    margin-bottom: ${getSpacing(s8)};

    @media (min-height: 850px) {
        height: 100px;
        margin-bottom: ${getSpacing(s16)};
    }
`;
const StyledNFamilyClubLogo = styled.img `
    height: 80px;
    width: auto;
    margin-bottom: 32px;

    @media (min-height: 850px) {
        height: 155px;
        margin-bottom: ${getSpacing(s16)};
    }
`;
const StyledKinderzimmerUKLogo = styled.img `
    height: 70px;
    width: auto;
    margin-bottom: ${props => props.theme.mf.spacing(8)};

    @media (min-height: 850px) {
        height: 155px;
        margin-bottom: ${props => props.theme.mf.spacing(6)};
    }
`;
const StyledCpcTreehouseLogo = styled.img `
    height: 84px;
    width: auto;
    margin-bottom: ${props => props.theme.mf.spacing(4)};

    @media (min-height: 850px) {
        height: 155px;
        margin-bottom: ${props => props.theme.mf.spacing(10)};
    }
`;
const StyledCpcOrchardLogo = styled.img `
    height: 84px;
    width: auto;
    margin-bottom: ${props => props.theme.mf.spacing(4)};

    @media (min-height: 850px) {
        height: 180px;
        margin-bottom: ${props => props.theme.mf.spacing(10)};
    }
`;
const StyledBBMontessoriLogo = styled.img `
    height: 60px;
    width: auto;
    margin-bottom: ${props => props.theme.mf.spacing(6)};

    @media (min-height: 850px) {
        height: 80px;
        margin-bottom: ${props => props.theme.mf.spacing(10)};
    }
`;
const StyledBBOdysseyLogo = styled.img `
    height: 60px;
    width: auto;
    margin-bottom: ${props => props.theme.mf.spacing(6)};

    @media (min-height: 850px) {
        height: 100px;
        margin-bottom: ${props => props.theme.mf.spacing(10)};
    }
`;
const StyledBusyBeesLogo = styled.img `
    height: 84px;
    width: auto;
    margin-bottom: ${props => props.theme.mf.spacing(4)};

    @media (min-height: 850px) {
        height: ${props => (props.hasAccounts ? 100 : 140)}px;
        margin-bottom: ${props => props.theme.mf.spacing(10)};
    }

    @media (min-height: 1000px) {
        height: 140px;
        margin-bottom: ${props => props.theme.mf.spacing(10)};
    }
`;
export default LoginChrome;
const BeforePosition = css `
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 40%;
`;
const BottomContainer = styled(createStack({ position: 'absolute' })) `
    right: 0px;
    bottom: 0px;

    padding-right: ${getSpacing(s1)};
    padding-bottom: calc(${getSpacing(s1)} + env(safe-area-inset-bottom));

    font-style: italic;
`;
const InnerContent = styled('div') `
    margin-top: auto;
    margin-bottom: auto;

    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 3;
    width: 100%;
    justify-content: flex-start;
    box-sizing: border-box;
    .slick-list {
        overflow: visible;
    }
    ${media.tablet `
        width: 100%;
        .slick-list {
            overflow: hidden;
        }
    `}
`;
const LayoutContainer = styled.div `
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${props => props.theme.invertedText};
    overflow-x: hidden;
`;
const Before = styled.div `
    flex-shrink: 0;
    background: ${props => props.theme.invertedText};
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    ${media.tablet `
        right: 0;
    `}
    ${media.mobile `
        &:before {
            display: none;
        }
    `}
    ${BeforePosition}
`;
const After = styled.div `
    overflow: hidden;
    background: ${props => props.theme.backgroundHover};
    flex-shrink: 0;
    z-index: 3;
    position: absolute;
    top: 0;
    bottom: 0;
    right: -3%;
    left: 60%;
    ${media.tablet `
        display: none;
    `}
    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        left: 0;
        bottom: 0;
        z-index: 2;
        box-shadow: 8px 0 16px 0 inset rgba(${props => hexToRGBString(props.theme.text)}, 0.1);
    }
`;
class Placeholder extends React.Component {
    get whitelabel() {
        return getWhitelabel();
    }
    get image() {
        if (!this.props.locale) {
            return undefined;
        }
        // if config is found, use it
        if (this.whitelabel) {
            const logoLogin = getAsset(this.whitelabel, 'loginBackground');
            if (logoLogin) {
                return `url(${logoLogin})`;
            }
        }
        // to support the original way
        switch (this.whitelabel) {
            case 'espira':
                return 'url(/img/espira/Espira_login_background.jpg)';
            case 'kido':
                return 'url(/img/kido/kido-login-background.jpg)';
            case 'brighthorizons':
                return 'url(/img/bright-horizons/login-background.jpg)';
            case 'grandiruk':
                return 'url(/img/grandiruk/login.jpg)';
            case 'nuffieldhealth':
                return 'url(/img/nuffieldhealth/login.jpg)';
            case 'nfamilyclub':
                return 'url(/img/nfamilyclub/login.jpg)';
            case 'kindred':
                return 'url(/img/kindred/login.jpg)';
            case 'kinderzimmeruk':
                return 'url(/img/kinderzimmeruk/login.jpg)';
            case 'cpctreehouse':
                return 'url(/img/cpc-treehouse/login.jpg)';
            case 'cpcorchards':
                return 'url(/img/cpc-orchard/login.jpg)';
            case 'busybees':
                return 'url(/img/busybees/login.jpg)';
            case 'bbmontessori':
                return 'url(img/bbmontessori/login.jpg)';
            case 'bbodyssey':
                return 'url(img/bbodyssey/login.jpg)';
            case 'famly':
            default:
                switch (this.props.locale) {
                    case 'de_DE':
                    case 'de_CH':
                        return 'url(img/login/background_de.jpg)';
                    case 'da_DK':
                        return 'url(img/login/background_dk.jpg)';
                    case 'en_GB':
                        if (this.props.loading) {
                            return undefined;
                        }
                        return 'url(img/login/background_gb.jpg)';
                    default:
                        return 'url(img/login/background_gb.jpg)';
                }
        }
    }
    render() {
        return _jsx(PlaceholderImage, { whitelabel: this.whitelabel, locale: this.props.locale, image: this.image });
    }
}
const placeHolderImagePosition = (props) => {
    switch (props.whitelabel) {
        case 'espira':
            return '65% center';
        case 'kido':
            return '50% center';
        case 'brighthorizons':
            return '70% center';
        case 'kindred':
            return '10% center';
        case 'nfamilyclub':
            return '50% center';
        case 'grandiruk':
            return '50% center';
        case 'nuffieldhealth':
            return '50% center';
        case 'kinderzimmeruk':
            return '43% center';
        case 'cpctreehouse':
            return 'right';
        case 'cpcorchards':
            return '40% center';
        case 'busybees':
            return '40% center';
        case 'bbmontessori':
            return '10% center';
        case 'bbodyssey':
            return '10% bottom';
        default:
            switch (props.image) {
                case 'url(img/login/background_de.jpg)':
                case 'url(img/login/background_dk.jpg)':
                    return '25% center';
                case 'url(img/login/background_gb.jpg)':
                    return '35% center';
                default:
                    return '50% center';
            }
    }
};
const PlaceholderImage = styled('div') `
    position: absolute;
    z-index: 1;
    top: 0px;
    bottom: 0px;
    left: -6%;
    right: -10px;
    background: ${props => { var _a; return (_a = props.image) !== null && _a !== void 0 ? _a : props.theme.invertedText; }};
    background-size: cover;
    background-position: ${placeHolderImagePosition};
    background-repeat: no-repeat;
`;
